import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Users, Award, TrendingUp, Shield, FileText, UserCheck, Star, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const benefits = [
  {
    icon: TrendingUp,
    title: '高额返利',
    description: '根据年度销售业绩，享受最高30%的销售返利',
  },
  {
    icon: Award,
    title: '等级权益',
    description: '铜银金三级经销商体系，等级越高权益越多',
  },
  {
    icon: Shield,
    title: '区域保护',
    description: '授权区域内独家经营权，避免恶性竞争',
  },
  {
    icon: Users,
    title: '专属支持',
    description: '专属客户经理，提供全方位业务支持',
  },
];

const levels = [
  {
    name: '铜牌经销商',
    color: 'from-amber-600 to-amber-400',
    requirements: ['注册资本50万以上', '年度销售额100万起', '完成基础培训'],
    benefits: ['5%基础返利', '区域保护', '营销物料支持'],
  },
  {
    name: '银牌经销商',
    color: 'from-gray-400 to-gray-300',
    requirements: ['年度销售额500万起', '团队规模10人以上', '铜牌满1年'],
    benefits: ['15%基础返利', '优先供货', '专属客户经理', '联合营销'],
  },
  {
    name: '金牌经销商',
    color: 'from-yellow-400 to-yellow-300',
    requirements: ['年度销售额2000万起', '团队规模30人以上', '银牌满2年'],
    benefits: ['30%基础返利', '全国区域可选', '战略客户共享', '年度峰会邀请'],
  },
];

const processSteps = [
  { step: 1, title: '在线申请', description: '填写企业基本信息，提交入驻申请', icon: FileText },
  { step: 2, title: '资质审核', description: '平台审核企业资质，3-5个工作日反馈', icon: UserCheck },
  { step: 3, title: '签约入驻', description: '签署经销协议，缴纳保证金', icon: Shield },
  { step: 4, title: '培训认证', description: '参加产品培训，通过认证考核', icon: Award },
  { step: 5, title: '正式经营', description: '获得授权，开始销售业务', icon: TrendingUp },
];

export default function Distribution() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative h-[400px] md:h-[500px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=600&fit=crop)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1c5bff]/90 to-[#1c5bff]/60" />
          <div className="relative h-full section-container flex items-center">
            <div className="text-white max-w-2xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">成为互联网生态平台经销商</h1>
              <p className="text-xl text-white/90 mb-8">
                加入我们的分销网络，共享平台资源，开启您的数字化事业新征程
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-8 py-4 bg-white text-[#1c5bff] font-semibold rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
                  立即申请入驻
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
                  了解更多
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">成为经销商的优势</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                我们提供全方位的支持体系，助力经销商快速成长
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="text-center p-6 rounded-2xl bg-gray-50 hover:bg-[#1c5bff]/5 transition-colors">
                  <div className="w-16 h-16 bg-[#1c5bff]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-[#1c5bff]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dealer Levels */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">经销商等级体系</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                三级晋升体系，业绩越好，权益越多
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {levels.map((level) => (
                <div key={level.name} className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
                  <div className={`bg-gradient-to-r ${level.color} p-6 text-white`}>
                    <div className="flex items-center gap-3 mb-2">
                      <Star className="w-6 h-6" />
                      <h3 className="text-2xl font-bold">{level.name}</h3>
                    </div>
                    <p className="text-white/80">年度评级，持续晋升</p>
                  </div>
                  <div className="p-6">
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">入驻要求</h4>
                      <ul className="space-y-2">
                        {level.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">专属权益</h4>
                      <ul className="space-y-2">
                        {level.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <CheckCircle className="w-5 h-5 text-[#1c5bff] flex-shrink-0 mt-0.5" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">入驻流程</h2>
              <p className="text-gray-600">简单五步，轻松成为平台经销商</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute top-8 left-0 right-0 h-0.5 bg-gray-200 hidden md:block" />
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                  {processSteps.map((step) => (
                    <div key={step.step} className="text-center relative">
                      <div className="w-16 h-16 bg-[#1c5bff] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                        <step.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="w-8 h-8 bg-white border-2 border-[#1c5bff] rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-sm font-bold text-[#1c5bff]">{step.step}</span>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#1c5bff]">
          <div className="section-container text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              准备好开启您的经销事业了吗？
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              立即申请入驻，成为互联网生态平台合作伙伴，共享数字化红利
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-white text-[#1c5bff] font-semibold rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
                立即申请入驻
                <ChevronRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
                下载入驻指南
              </button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">常见问题</h2>
              <p className="text-gray-600">关于经销商入驻的常见问题解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                {
                  q: '入驻需要缴纳费用吗？',
                  a: '入驻申请免费，签约时需缴纳一定金额的保证金，保证金在合作结束后可退还。',
                },
                {
                  q: '经销商有区域限制吗？',
                  a: '是的，每位经销商都有授权经营区域，在授权区域内享有独家经营权。',
                },
                {
                  q: '如何晋升为更高级别的经销商？',
                  a: '根据年度销售业绩和综合评估，符合条件的经销商可自动晋升到下一级别。',
                },
                {
                  q: '平台提供哪些培训支持？',
                  a: '平台提供产品知识培训、销售技巧培训、市场营销培训等全方位培训支持。',
                },
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
